/*
 *   File : portfolio.js
 *   Author : https://evoqins.com
 *   Description : portfolio page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';

// Components
import { APIService } from '../../Service';
import { _getProfile, _goalSummary, _mfSummary } from '../../Helper';
import { Footer } from '../../Component/Footer';
import { Chip, EmptyScreen } from '../../Component/Other';
import { DetailPreviewCard, FilterCard, PortfolioCard, } from '../../Component/Cards';
import { Icon } from '../../Component/Icon';
import { SectionTitle } from '../../Component/Title';
import Holdings from '../../Component/Cards/holdings';
import OverLay from "../../Component/Other/overlay";
import { PrimaryButton, SecondaryButton, TextButton } from '../../Component/Buttons';
import { ExternalFundImportModal, GoalInvestModal, MFInvestModal, OffcanvasDetailViewModal, PaymentStatusModal, PortfolioFilterModal, RedeemModal, RedeemStatusModal, UpdateNomineeInfoModal } from '../../Component/Modal';


// Assets
import Loader from "../../Assets/Images/coloured_loader.svg";
import EmptyPortfolio from "../../Assets/Images/EmptyScreen/no-portfolio.webp";
import { _openOffcanvasModal } from '../../Helper/function';
import { _generateMFImportURL } from '../../Helper/api';


// Custom Data
const BREADCRUMBS = [
    {
        label: "Home",
        path: "/"
    },
    {
        label: "Portfolio",
        path: "/portfolio"
    },
];

const FILTER_PORTFOLIO_FOR = [
    {
        id: null,
        label: "All"
    },
    {
        id: -1,
        label: "My portfolio"
    },
];

const DEFAULT_FILTER_INVESTMENT_TYPE = {
    id: null,
    label: "All"
};

const DEFAULT_FILTER_PORTFOLIO_FOR = {
    id: -1,
    label: "My portfolio"
};

const Portfolio = () => {

    const { USER_DATA: PROFILE_DATA, IS_NOMINEE_UPDATED, FILTER_OPTIONS_FAMILY } = useSelector(store => store.Reducer);
    const is_nri = PROFILE_DATA && PROFILE_DATA.phone.country_code === "+91" ? false : true;
    const applied_filters = JSON.parse(localStorage.getItem("portfolio_filters"));

    const navigate = useNavigate();
    const location = useLocation();
    const initialRender = useRef(true);

    const [portfolioSummary, setPortfolioSummary] = useState({});
    const [holdings, setHoldings] = useState([]);
    const [toggleFilterModal, setToggleFilterModal] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [isHoldingsDataLoading, setIsHoldingsDataLoading] = useState(false);

    // Invest
    const [fundSummary, setFundSummary] = useState({});
    const [fundId, setFundId] = useState("");
    const [goalSummary, setGoalSummary] = useState({});
    const [goalId, setGoalId] = useState("");
    const [showInvestModal, setShowInvestModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentModalType, setPaymentModalType] = useState(0);
    const [investType, setInvestType] = useState("");
    const available_investment_types = {
        sip: true,
        lumpsum: true,
    };

    // Redeem Invest
    const [redeemSummary, setRedeemSummary] = useState({});
    const [showRedeemModal, setShowRedeemModal] = useState(false);
    const [showRedeemStatusModal, setShowRedeemStatusModal] = useState(false);
    const [showImportPortfolioModal, setShowImportPortfolioModal] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [isImportInProgress, setIsImportInProgress] = useState(false);
    const [isExternalFund, setIsExternalFund] = useState(false);
    const [showNomineeInfoModal, setShowNomineeInfoModal] = useState(false);
    const [selectedHoldingsData, setSelectedHoldingsData] = useState({});
    const [filterOptionsPortfolioFor, setFilterOptionsPortfolioFor] = useState(FILTER_PORTFOLIO_FOR);
    const [selectedFund, setSelectedFund] = useState({});
    const [isCardView, setIsCardView] = useState(applied_filters && applied_filters?.is_card_view ? applied_filters.is_card_view : false);
    const [filterPortfolioFor, setFilterPortfolioFor] = useState(applied_filters && applied_filters?.selected_portfolio_for_filter ? applied_filters.selected_portfolio_for_filter : DEFAULT_FILTER_PORTFOLIO_FOR);
    const [filterInvestmentType, setFilterInvestmentType] = useState(applied_filters && applied_filters?.selected_investment_type_filter ? applied_filters.selected_investment_type_filter : DEFAULT_FILTER_INVESTMENT_TYPE);


    const [showValues, setShowValues] = useState(localStorage.getItem(btoa("show_values_portfolio")) ? localStorage.getItem(btoa("show_values_portfolio")) === "true" : true);


    // API call 
    useEffect(() => {

        setIsDataLoading(true);
        _getHoldingsData(); // portfolio data 

        window.scrollTo({
            top: 0, behavior: "smooth"
        });

        window.addEventListener("click", (e) => {
            if (!e.target.closest("#portfolio-filter")) {
                setToggleFilterModal(false);
            }
        });

    }, []);

    // for filters
    useEffect(() => {
        setFilterOptionsPortfolioFor([...FILTER_PORTFOLIO_FOR, ...FILTER_OPTIONS_FAMILY])
    }, [FILTER_OPTIONS_FAMILY]);

    useEffect(() => {
        if (toggleFilterModal === true) {
            document.body.style.overflow = "hidden";
            document.querySelector("#portfolio-filter").scrollIntoView({ top: 0, behavior: "smooth" });
        } else {
            document.body.style.overflow = "unset";
        };

        // Removing backdrop form UI when user goes back in history while filter modals opened
        window.addEventListener('popstate', function () {
            setToggleFilterModal(false);
            setShowRedeemModal(false);
            setShowPaymentModal(false);
            setShowInvestModal(false);
        });

    }, [toggleFilterModal, showRedeemModal, showInvestModal, showPaymentModal]);

    useEffect(() => {
        if (initialRender.current === false) {
            _getHoldingsData();

            // Saving applied filters in localstorage
            localStorage.setItem("portfolio_filters", JSON.stringify({
                "is_card_view": isCardView,
                "selected_portfolio_for_filter": filterPortfolioFor,
                "selected_investment_type_filter": filterInvestmentType,
            }));
        }

    }, [filterPortfolioFor, filterInvestmentType]);

    useEffect(() => {

        if (showInvestModal !== true) return;

        switch (investType) {
            case "Fund": {
                new Modal(document.getElementById('invest-mf-modal')).show();
                break;
            };
            case "Goal": {
                new Modal(document.getElementById('invest-goal-modal')).show();
                break;
            };
            case "Basket": {
                // new Modal(document.getElementById('invest-basket-modal')).show();
                new Modal(document.getElementById('invest-goal-modal')).show();
                break;
            };
            default: return null;
        }
    }, [showInvestModal]);

    useEffect(() => {
        if (showPaymentModal == true) {
            new Modal(document.querySelector("#payment-status-modal")).show();
        }
    }, [showPaymentModal]);

    useEffect(() => {
        if (showRedeemModal !== true) return;
        new Modal(document.getElementById('redeem-invest-modal')).show();
    }, [showRedeemModal]);

    useEffect(() => {
        if (showRedeemStatusModal) {
            new Modal(document.getElementById('redeem-status-modal')).show();
        }
    }, [showRedeemStatusModal]);

    useEffect(() => {
        if (showImportPortfolioModal === true) {
            let import_portfolio_modal = new Modal(document.getElementById('import-portfolio-modal'));
            import_portfolio_modal.show();
        }
    }, [showImportPortfolioModal]);

    useEffect(() => {
        if (showNomineeInfoModal) {
            new Modal(document.querySelector("#update-nominee")).show();
        }
    }, [showNomineeInfoModal]);


    const _handlePortfolioFilterModal = () => {
        setToggleFilterModal(status => status == false ? true : false);
    };

    const _handleCloseFilterModal = () => {
        setToggleFilterModal(false);
    };

    const _handleShowRefreshImport = () => {
        // setShowImportPortfolioModal(true);

        setRefreshLoading(true);

        _generateMFImportURL().then((response) => {
            if (response?.status_code === 200) {
                let redirect_url = response?.data?.redirectUrl;
                window.open(redirect_url, "_self");

            } else {
                toast.dismiss();
                toast.error(response.message);
            }
        }).finally(() => setRefreshLoading(false));


    };

    const _handleApplyFilters = (selected_portfolio_for, selected_investment_type, status) => {
        if (status == true) {
            setFilterPortfolioFor({ ...selected_portfolio_for });
            setFilterInvestmentType({ ...selected_investment_type });
            setIsDataLoading(true);
        }
        setToggleFilterModal(false);
    };

    const _handleCloseInvestModal = (type) => {

        // Closing modal
        let button = "";
        switch (investType) {
            case "Fund": {
                button = document.getElementById("mf-invest-modal-close");
                break;
            };
            case "Goal": {
                button = document.getElementById("goal-invest-modal-close");
                break;
            };
            case "Basket": {
                // button = document.getElementById("basket-invest-modal-close");
                button = document.getElementById("goal-invest-modal-close");
                break;
            };
            default: return null;
        };

        button.click();
        setShowInvestModal(false);

        if (type) {
            setShowPaymentModal(true);
            setPaymentModalType(type);
        };
    };

    const _handleCloseRedeemModal = (type) => {
        let button = document.getElementById("redeem-invest-modal-close");
        button.click();
        setShowRedeemModal(false);

        if (type === "REDEEM_SUCCESS") {
            setShowRedeemStatusModal(true);
        };
    };

    // Handle investmore on holdings
    const _handleInvestMore = (e, id, type) => {

        // if (IS_NOMINEE_UPDATED === false) {
        //     setShowNomineeInfoModal(true);
        //     return;
        // }

        e.stopPropagation();
        setInvestType(type);

        if (type === "Fund") {
            _getMFSummary(id);
        } else {
            setGoalId(id);
            _getGoalSummary(id);
        };
    };

    // Handle redeem on holdings
    const _handleRedeemInvest = (e, id, type) => {

        e.stopPropagation();
        setInvestType(type);

        if (type == "Fund") {
            setFundId(id);
            _getMFRedeemOverview(id);
        } else {
            setGoalId(id);
            _getGoalRedeemOverview(id);
        };
    };

    const _handleToggleShowValues = () => {
        setShowValues(prev => {
            localStorage.setItem(btoa("show_values_portfolio"), !prev);
            return !prev
        });
    };

    const _handleChangeHoldingsView = () => {
        setIsCardView(prev => !prev);
    };

    // handle close import portfolio modal
    const _handleCloseImportPortfolioModal = () => {
        setShowImportPortfolioModal(false);
    };

    // handle update portfolio
    const _handleUpdatePortfolio = () => {
        setShowImportPortfolioModal(false);
        setIsImportInProgress(true);
    };

    const _handleSelectFund = (selected_fund) => {

        const current_fund_id = selectedFund.type == "Fund" ? selectedFund.mf_id : selectedFund.goal_id;
        const new_fund_id = selected_fund.type == "Fund" ? selected_fund.mf_id : selected_fund.goal_id;

        if (current_fund_id !== new_fund_id) {
            setSelectedFund(selected_fund);
            // API call
            setIsHoldingsDataLoading(true);
            _getSelectedHoldingsData(selected_fund);
        } else {
            if (selectedFund?.name) {
                _openOffcanvasModal();
            }
        }
    };

    const _handleNavigateToAnalysis = () => {
        navigate("analysis", {
            state: {
                selected_portfolio_for_filter: filterPortfolioFor,
                selected_investment_type_filter: filterInvestmentType,
                filter_options_portfolio_for: filterOptionsPortfolioFor
            }
        });
    }

    const _handleNavigateToSIP = () => {
        navigate("/sip");
        localStorage.setItem("sip_filters", JSON.stringify({
            "selected_portfolio_for_filter": filterPortfolioFor,
        }));
    };




    //==================================== //
    //=========== API CAllS ============== //

    // API - Get holding from the selected filter
    const _getHoldingsData = () => {
        let api_version = 2;
        let is_post = true;
        let url = 'dashboard/holdings';
        let data = {
            "investment_type_filter": filterInvestmentType?.id,
            "portfolio_for": filterPortfolioFor?.id,

            // old filters 
            "investment_type": null,
            "filter": null,
        };

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                setPortfolioSummary({ ...response.data });
                setHoldings([...response.data.holdings]);
            } else {
                setPortfolioSummary({});
                setHoldings([]);
            };

            setSelectedHoldingsData({});
            setSelectedFund({});
            setIsHoldingsDataLoading(false);
            setIsDataLoading(false);
            initialRender.current = false;
        });
    };

    // API - Getting Fund Summary for reinvesting in MF
    const _getMFSummary = (id) => {
        const param = {
            "mf_id": id,
            "type": "REINVEST",
        };

        // Api call
        _mfSummary(param).then(status => {
            if (status.status_code === 200) {
                setFundSummary(status.data);
                setShowInvestModal(true);
            } else {
                toast.dismiss();
                toast.error(status.message, { className: "e-toast", });
            }
        });
    };

    // API - Getting Redeem details for selected fund
    const _getMFRedeemOverview = (id) => {
        let is_post = true;
        let url = 'mf/redeem-overview';
        let data = {
            "mf_id": id
        };

        APIService(is_post, url, data).then(response => {
            if (response.status_code == 200) {
                setRedeemSummary(response.data);
                setShowRedeemModal(true);
            } else {
                setRedeemSummary({});
                toast.dismiss();
                toast.error(response.message, { className: "e-toast", });
            };
        });
    };

    //API - Getting goal summary for reinvesting in goal or basket
    const _getGoalSummary = (id) => {

        let param = {
            "goal_id": id,
            "type": "REINVEST",
        };

        _goalSummary(param).then(response => {
            if (response.status_code === 200) {
                setShowInvestModal(true);
                setGoalSummary(response.data);
            } else {
                setGoalSummary({});
                toast.dismiss();
                toast.error(response.message, { className: "e-toast", });
            };
        });
    };

    // API - Getting Redeem details for selected goal or basket
    const _getGoalRedeemOverview = (id) => {
        let is_post = true;
        let url = 'goal/redeem-overview';
        let data = {
            "goal_id": id
        };

        APIService(is_post, url, data).then(response => {
            if (response.status_code == 200) {
                setRedeemSummary(response.data);
                setShowRedeemModal(true);
            } else {
                setRedeemSummary({});
                toast.dismiss();
                toast.error(response.message, { className: "e-toast", });
            };
        });
    };

    // API - Post Selected MF Details
    const _getSelectedHoldingsData = (selected_fund) => {
        let api_version = 2;
        let is_post = true;
        let url = selected_fund?.type === "Fund" ? 'dashboard/fund' : 'dashboard/goal';
        let data = {
            [selected_fund?.type === "Fund" ? "mf_id" : "goal_id"]: selected_fund?.type === "Fund" ? selected_fund?.mf_id : selected_fund?.goal_id,
            family_detail_id: selected_fund?.member_id || null
        };

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                setSelectedHoldingsData(response.data);
                // Scrolling to view
                if (document.querySelector("#detailed-fund-view")) {
                    let detailed_card_wrapper = document.querySelector("#detailed-fund-view");
                    detailed_card_wrapper.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                if (window.innerWidth < 768) {
                    _openOffcanvasModal();
                }
            } else {
                setSelectedHoldingsData({});
                setSelectedFund({});
                toast.dismiss("");
                toast.error(response.message);
            }

            setIsHoldingsDataLoading(false);
        });
    };


    // Inner component
    const DetailedViewCard = () => {
        return (
            <>

                {
                    isHoldingsDataLoading == true ?
                        <div className="e-border-1px-white-marble e-border-radius-24 p-3 pt-5 text-center pb-5" >
                            <img src={Loader}
                                draggable="false"
                                alt="loader" />
                        </div>
                        :
                        <>
                            {
                                selectedFund?.name ?
                                    selectedFund.type === "Fund" ?
                                        <div className="e-border-1px-white-marble e-border-radius-24 p-3">
                                            <div className="e-bg-cultured e-border-radius-16 p-3">
                                                <div className={`d-flex gap-2 mb-3`}>
                                                    <img src={selectedHoldingsData?.icon}
                                                        width={32}
                                                        height={32}
                                                        className="object-fit-contain e-border-radius-8 e-bg-white p-1"
                                                        draggable="false"
                                                        alt="fund image" />
                                                    <div className="">
                                                        <h3 className="e-ellipse e-text-charleston-green e-font-10 e-alt-font-poppins e-font-weight-400 line-height-14px margin-12px-bottom">
                                                            {selectedHoldingsData?.name}
                                                        </h3>

                                                        <div className="d-flex flex-wrap gap-2">
                                                            <Chip className={"py-1 px-3 e-font-10 line-height-14px"}
                                                                name={selectedHoldingsData?.asset_category} />

                                                            {
                                                                selectedFund.type !== null &&
                                                                <Chip className={"py-1 px-3 e-font-10 line-height-14px"}
                                                                    name={selectedHoldingsData?.option_name} />
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-between mb-3">
                                                    <div className="col-auto d-flex flex-column">
                                                        <span className="e-text-dark-heather  e-alt-font-poppins  e-font-14 e-font-weight-400 line-height-22px">
                                                            Invested
                                                        </span>
                                                        <span className="e-text-charleston-green e-primary-font-inter  e-font-14 e-font-weight-600 line-height-22px">
                                                            {selectedHoldingsData?.summary?.invested_amount_text}
                                                        </span>
                                                    </div>
                                                    <div className="col-auto d-flex flex-column text-end">
                                                        <span className="e-text-dark-heather  e-alt-font-poppins  e-font-14 e-font-weight-400 line-height-22px">
                                                            Current
                                                        </span>
                                                        <span className="e-text-charleston-green e-primary-font-inter  e-font-14 e-font-weight-600 line-height-22px">
                                                            {selectedHoldingsData?.summary?.current_amount_text}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="d-flex flex-wrap justify-content-between mb-3">
                                                    <div className="col-auto d-flex flex-column">
                                                        <span className="e-text-dark-heather  e-alt-font-poppins  e-font-14 e-font-weight-400 line-height-22px">
                                                            Returns
                                                        </span>
                                                        <span className={`${selectedHoldingsData?.summary?.returns_amount >= 0 ? "e-text-bottle-green" : "e-text-red"} e-text-bottle-green d-flex flex-wrap e-primary-font-inter  e-font-14 e-font-weight-600 line-height-22px`}>
                                                            <span>
                                                                {selectedHoldingsData?.summary?.returns_amount_text}
                                                            </span>
                                                            <span className="e-font-14 ps-1">
                                                                ({selectedHoldingsData?.summary?.returns_percentage_text})
                                                            </span>
                                                        </span>
                                                    </div>
                                                    {
                                                        selectedHoldingsData?.summary?.xirr_text &&
                                                        <div className="col-auto d-flex flex-column text-end">
                                                            <span className="e-text-dark-heather  e-alt-font-poppins e-font-14 e-font-weight-400 line-height-22px">
                                                                XIRR
                                                            </span>
                                                            <span className={`${selectedHoldingsData?.summary?.xirr >= 0 ? "e-text-bottle-green" : "e-text-red"} e-primary-font-inter  e-font-14 e-font-weight-600 line-height-22px`}>
                                                                {selectedHoldingsData?.summary?.xirr_text}
                                                            </span>
                                                        </div>
                                                    }
                                                </div>
                                                <div className="d-flex justify-content-between mb-3">
                                                    <div className="col-auto d-flex flex-column">
                                                        <span className="e-text-dark-heather  e-alt-font-poppins  e-font-14 e-font-weight-400 line-height-22px">
                                                            Avg. NAV
                                                        </span>
                                                        <span className="e-text-charleston-green e-primary-font-inter  e-font-14 e-font-weight-600 line-height-22px">
                                                            {selectedHoldingsData?.summary?.average_nav_text || "-"}
                                                        </span>
                                                    </div>
                                                    <div className="col-auto d-flex flex-column text-end">
                                                        <span className="e-text-dark-heather  e-alt-font-poppins  e-font-14 e-font-weight-400 line-height-22px">
                                                            Units
                                                        </span>
                                                        <span className="e-text-charleston-green e-primary-font-inter  e-font-14 e-font-weight-600 line-height-22px">
                                                            {selectedHoldingsData?.summary?.units || "-"}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <div className="col-auto d-flex flex-column">
                                                        <span className="e-text-dark-heather word-break-all text-wrap e-alt-font-poppins  e-font-14 e-font-weight-400 line-height-22px">
                                                            Cur. NAV (24 Jun 2023)
                                                        </span>
                                                        <span className="e-text-charleston-green e-primary-font-inter  e-font-14 e-font-weight-600 line-height-22px">
                                                            {selectedHoldingsData?.summary?.current_nav_text || "-"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {
                                                selectedHoldingsData?.summary?.active_sips.length > 0 &&
                                                <div className='mt-3'>
                                                    <SectionTitle title="Active SIP's" fontFamily="e-primary-font-inter" fontSize="e-font-14" />
                                                    <div className='overflow-scroll no-scrollbar mt-2 e-bg-cultured e-border-radius-16 p-3'>
                                                        <div className='overflow-scroll no-scrollbar d-flex flex-column row-gap-12px'>
                                                            <div className='d-flex gap-2 justify-content-between'>
                                                                <h6 className='w-30 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                    Amount
                                                                </h6>
                                                                <h6 className='w-35 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                    SIP date
                                                                </h6>
                                                                <h6 className='w-35 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                    Next SIP date
                                                                </h6>
                                                            </div>

                                                            {
                                                                selectedHoldingsData?.summary?.active_sips.map((item, key) => (
                                                                    <div className='d-flex gap-2 justify-content-between'
                                                                        key={key}>
                                                                        <p className='w-30 text-nowrap mb-0 e-text-charleston-green e-primary-font-inter e-font-12 e-font-weight-500 line-height-20px'>
                                                                            {item.amount_text}
                                                                        </p>
                                                                        <p className='w-35 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-400 line-height-20px'>
                                                                            {item.sip_date}
                                                                        </p>
                                                                        <p className='w-35 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-500 line-height-20px'>
                                                                            {item.next_due}
                                                                        </p>
                                                                    </div>
                                                                ))
                                                            }

                                                        </div>
                                                    </div>
                                                </div>
                                            }

                                            <div className="py-3 mt-3 mb-3 e-border-bottom-1px-white-marble e-border-top-1px-white-marble">
                                                <TextButton name="Manage SIP"
                                                    wrapperClassName="d-flex justify-content-between"
                                                    onClick={_handleNavigateToSIP} />
                                            </div>

                                            {
                                                selectedHoldingsData?.transactions?.length !== 0 &&
                                                <div className="pb-3 e-border-bottom-1px-white-marble">
                                                    <h6 className="e-alt-font-poppins e-font-16 e-font-14-res mb-3 e-text-charleston-green e-font-weight-500 line-height-24px">
                                                        Transaction history
                                                    </h6>
                                                    <div className="d-flex flex-column gap-3">
                                                        {
                                                            (selectedHoldingsData?.transactions || []).map((item, index) => (
                                                                <div key={index} className="row justify-content-between">
                                                                    <div className="col-4">
                                                                        <div className="d-flex flex-column">
                                                                            <h6 className='mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                                Amount
                                                                            </h6>
                                                                            <span className="e-text-dark-heather  e-alt-font-poppins  e-font-12 e-font-weight-400 line-height-22px">
                                                                                {item.invested_date}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-8">
                                                                        <div className="d-flex flex-column align-items-end">
                                                                            <h6 className=' mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                                {item.amount_text}
                                                                            </h6>
                                                                            <span className="text-end e-text-dark-heather e-alt-font-poppins  e-font-12 e-font-weight-400 line-height-22px">
                                                                                {item.units}  {item.units > 1 ? "units" : "unit"}  / {item.nav_text} NAV
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            }

                                            {
                                                selectedFund.show_detail &&
                                                <div className="row gx-3 row-gap-3 flex-wrap align-items-center mt-4">
                                                    <div className="col">

                                                        <SecondaryButton name="Redeem"
                                                            disabled={selectedFund.current_amount > 0 ? false : true}
                                                            className="w-100 py-2 py-sm-3 e-bg-lotion e-font-14 e-text-charleston-green e-border-bright-gray"
                                                            onPress={(e) => _handleRedeemInvest(e, selectedHoldingsData.mf_id, "Fund")} />
                                                    </div>
                                                    <div className="col">

                                                        <PrimaryButton name="Invest more"
                                                            className="w-100 e-border-radius-24  e-font-14-res e-font-14 py-sm-3 py-2 e-font-weight-600 text-nowrap"
                                                            onPress={(e) => _handleInvestMore(e, selectedHoldingsData.mf_id, "Fund")} />
                                                    </div>
                                                </div>
                                            }

                                        </div>
                                        :
                                        <div className="e-border-1px-white-marble e-border-radius-24 p-3">
                                            <div className="e-bg-cultured e-border-radius-16 p-3">
                                                <div className={`d-flex align-items-center gap-2 mb-3`}>
                                                    <img src={selectedHoldingsData.icon}
                                                        width={32}
                                                        height={32}
                                                        className="object-fit-contain e-border-radius-8 e-bg-white p-1"
                                                        draggable="false"
                                                        alt="fund image" />
                                                    <h3 className="e-ellipse e-text-charleston-green e-font-12-res e-font-10 e-alt-font-poppins e-font-weight-400 line-height-14px mb-0">
                                                        {selectedHoldingsData.name}
                                                    </h3>
                                                </div>
                                                <div className="d-flex justify-content-between mb-sm-3 mb-2">
                                                    <div className="col-auto d-flex flex-column">
                                                        <span className="e-text-dark-heather  e-alt-font-poppins e-font-12-res  e-font-14 e-font-weight-400 line-height-22px">
                                                            Invested
                                                        </span>
                                                        <span className="e-text-charleston-green e-primary-font-inter  e-font-12-res  e-font-14 e-font-weight-600 line-height-22px">
                                                            {selectedHoldingsData?.summary?.invested_amount_text || "-"}
                                                        </span>
                                                    </div>
                                                    <div className="col-auto d-flex flex-column text-end">
                                                        <span className="e-text-dark-heather  e-alt-font-poppins  e-font-12-res e-font-14 e-font-weight-400 line-height-22px">
                                                            Current
                                                        </span>
                                                        <span className="e-text-charleston-green e-primary-font-inter e-font-12-res  e-font-14 e-font-weight-600 line-height-22px">
                                                            {selectedHoldingsData?.summary?.current_amount_text || "-"}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="d-flex flex-wrap justify-content-between">
                                                    <div className="col-auto d-flex flex-column">
                                                        <span className="e-text-dark-heather  e-alt-font-poppins  e-font-12-res e-font-14 e-font-weight-400 line-height-22px">
                                                            Total Returns
                                                        </span>
                                                        <span className={`${selectedHoldingsData?.summary?.returns_amount >= 0 ? "e-text-bottle-green" : "e-text-red"} e-text-bottle-green d-flex flex-wrap e-primary-font-inter  e-font-12-res e-font-14 e-font-weight-600 line-height-22px`}>
                                                            <span>
                                                                {selectedHoldingsData?.summary?.returns_amount_text}
                                                            </span>
                                                            <span className="e-font-12-res e-font-14 ps-1">
                                                                ({selectedHoldingsData?.summary?.returns_percentage_text})
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {
                                                selectedHoldingsData?.summary?.active_sips.length > 0 &&
                                                <div className='mt-3'>
                                                    <SectionTitle title="Summary" fontFamily="e-primary-font-inter" fontSize="e-font-14" />
                                                    <div className='overflow-scroll no-scrollbar mt-2 e-bg-cultured e-border-radius-16 p-3'>
                                                        <div className='overflow-scroll no-scrollbar d-flex flex-column row-gap-12px'>
                                                            <div className='d-flex gap-2 justify-content-between'>
                                                                <h6 className='w-30 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                    Amount
                                                                </h6>
                                                                <h6 className='w-35 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                    SIP date
                                                                </h6>
                                                                <h6 className='w-35 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                    Next SIP date
                                                                </h6>
                                                            </div>

                                                            {
                                                                selectedHoldingsData?.summary?.active_sips.map((item, key) => (
                                                                    <div className='d-flex gap-2 justify-content-between'
                                                                        key={key}>
                                                                        <p className='w-30 text-nowrap mb-0 e-text-charleston-green e-primary-font-inter e-font-12 e-font-weight-500 line-height-20px'>
                                                                            {item.amount_text}
                                                                        </p>
                                                                        <p className='w-35 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-400 line-height-20px'>
                                                                            {item.sip_date}
                                                                        </p>
                                                                        <p className='w-35 text-nowrap mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-500 line-height-20px'>
                                                                            {item.next_due}
                                                                        </p>
                                                                    </div>
                                                                ))
                                                            }

                                                        </div>
                                                    </div>
                                                </div>
                                            }

                                            <div className='mt-3'>
                                                <SectionTitle title="Your investment allocations" fontFamily="e-primary-font-inter" fontSize="e-font-14" />
                                                <div className='overflow-scroll no-scrollbar mt-2 e-bg-cultured e-border-radius-16 p-3'>
                                                    <div className='overflow-scroll no-scrollbar d-flex flex-column row-gap-12px'>
                                                        <div className='d-flex  justify-content-between overflow-scroll no-scrollbar w-max-content'>
                                                            <h6 className='fund w-75px  mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                Fund name
                                                            </h6>
                                                            <h6 className='weight w-75px  mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                Weightage
                                                            </h6>
                                                            <h6 className='xirr w-40px  mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                XIRR
                                                            </h6>
                                                            <h6 className='current_value w-65px  mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-600 line-height-14px'>
                                                                Cur.value
                                                            </h6>
                                                        </div>

                                                        {
                                                            (selectedHoldingsData?.funds || []).map((item, key) => (
                                                                <div className='d-flex justify-content-between overflow-scroll no-scrollbar w-max-content'
                                                                    key={key}>
                                                                    <p className='fund w-75px  mb-0 e-text-charleston-green e-primary-font-inter e-font-12 e-font-weight-500 line-height-20px padding-2-right'>
                                                                        {item.scheme_name}
                                                                    </p>
                                                                    <p className='weight w-75px  mb-0 e-text-charleston-green e-alt-font-poppins e-font-12 e-font-weight-400 line-height-20px'>
                                                                        {item.allocation_text}
                                                                    </p>
                                                                    <p className='xirr w-40px  word-break-all mb-0 e-text-bottle-green e-alt-font-poppins e-font-12 e-font-weight-500 line-height-20px'>
                                                                        {item.xirr_text || "-"}
                                                                    </p>
                                                                    <p className='current_value w-65px  mb-0 e-text-charleston-green e-primary-font-inter e-font-12 e-font-weight-500 line-height-20px'>
                                                                        {item.current_amount_text}
                                                                    </p>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            </div>


                                            {
                                                selectedFund.show_detail &&
                                                <div className="row gx-3 flex-wrap row-gap-3 align-items-center mt-4">
                                                    <div className="col">

                                                        <SecondaryButton name="Redeem"
                                                            disabled={selectedFund.current_amount > 0 ? false : true}
                                                            className="w-100 py-sm-3 py-2 e-bg-lotion e-font-14 e-text-charleston-green e-border-bright-gray"
                                                            onPress={(e) => _handleRedeemInvest(e, selectedHoldingsData.goal_id, "Goal")} />
                                                    </div>
                                                    <div className="col">

                                                        <PrimaryButton name="Invest more"
                                                            className="w-100 e-border-radius-24  e-font-14-res e-font-14 py-sm-3 py-2 e-font-weight-600 text-nowrap"
                                                            onPress={(e) => _handleInvestMore(e, selectedHoldingsData.goal_id, "Goal")} />
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    :
                                    <DetailPreviewCard title={"Pick a fund to get started"}
                                        description=" Detailed insights will appear here once you choose a fund." />
                            }
                        </>
                }
            </>
        )
    };

    return (
        <>
            <div className="row min-h-100vh pb-4 pb-sm-5 align-items-start e-bg-lotion px-4 px-sm-5 ps-lg-4 pe-lg-0 pt-4 e-border-radius-TL-BL-24px">

                {
                    isDataLoading ? (
                        <div className="min-h-100vh text-center mt-5" >
                            <img src={Loader}
                                draggable="false"
                                alt="loader" />
                        </div>
                    ) : (
                        <div className="col-lg-11 p-0">

                            <div className='px-0'>
                                <div className="row justify-content-end">
                                    <div className="col-auto">
                                        <div className={`position-relative`} id="portfolio-filter">
                                            <FilterCard filterText={`${filterPortfolioFor?.label} ${filterInvestmentType.id && filterInvestmentType.id !== null ? ", +1" : ""}`}
                                                handleOpenFilterModal={_handlePortfolioFilterModal} />
                                            {/* filter modal */}
                                            {
                                                toggleFilterModal &&
                                                <>
                                                    <OverLay isOpen={toggleFilterModal} onClose={_handleCloseFilterModal} />
                                                    <PortfolioFilterModal
                                                        filterOptionsPortfolioFor={filterOptionsPortfolioFor}
                                                        selectedFilterPortfolioFor={filterPortfolioFor}
                                                        selectedFilterInvestmentType={filterInvestmentType}
                                                        onClick={_handleCloseFilterModal}
                                                        handleSelectedFilters={_handleApplyFilters} />
                                                </>

                                            }
                                        </div>
                                    </div>
                                </div>
                                {/* Portfolio summary card */}
                                <PortfolioCard type={1}
                                    wrapperClassName={`mt-4 ${!portfolioSummary.external_portfolio_imported ? "margin-32px-bottom" : ""}`}
                                    title={"Current investment"}
                                    summary={portfolioSummary.summary}
                                    showValues={showValues}
                                    handleToggleShowValues={_handleToggleShowValues}
                                    handleNavigateToAnalysis={_handleNavigateToAnalysis} />

                                {/* Refresh External import */}
                                {
                                    portfolioSummary?.show_external_portfolio_banner &&
                                    portfolioSummary?.external_portfolio_imported &&
                                    <div className="d-flex justify-content-center gap-2 align-items-center margin-32px-bottom mt-4">
                                        <SecondaryButton type={2}
                                            icon="import-arrow"
                                            size={16}
                                            loading={refreshLoading}
                                            disabled={refreshLoading}
                                            name={isImportInProgress ? "Updating external funds" : `Refresh external funds (${portfolioSummary?.external_portfolio_holdings_count})`}
                                            className={`${isImportInProgress ? "pe-none cursor-default" : ""} padding-14px-tb col-4 d-flex justify-content-center align-items-center gap-2`}
                                            onPress={_handleShowRefreshImport} />
                                        <span className='d-inline-flex ms-1 e-font-12 e-text-charleston-green e-alt-font-poppins e-font-weight-400 line-height-14px'>
                                            {isImportInProgress ? "This may take 10-15 minutes" : `Last update: ${portfolioSummary?.external_portfolio_imported_on}`}
                                        </span>
                                    </div>
                                }

                                <div className="row gx-gy-32px ">
                                    <div className="col-12 col-md-8">
                                        {/* Holdings */}
                                        <div className='ps-0'>
                                            <div className='d-flex justify-content-between align-items-center mt-4 mb-sm-4 mb-3'>
                                                <SectionTitle title={filterPortfolioFor.id === -1 ? "Your Holdings" : filterPortfolioFor.id === null ? "All Holdings" : `${filterPortfolioFor.label} Holdings`}
                                                    fontFamily=" e-primary-font-inter  e-text-eerie-black e-font-20" />

                                                <Icon icon={isCardView ? "list-view" : "card-view"}
                                                    className="cursor-pointer w-24px width-16px-res"
                                                    onClick={_handleChangeHoldingsView} />
                                            </div>

                                            {

                                                holdings && holdings.length !== 0 ? (
                                                    <div className={`row p-0 flex-wrap  ${isCardView ? "row-gap-4" : "row-gap-3"}`}>
                                                        {
                                                            holdings.map((item, key) => (
                                                                <>
                                                                    <div className={`${isCardView ? "col-sm-6 col-12" : "col-12"}`}>
                                                                        <Holdings data={item}
                                                                            onInvest={_handleInvestMore}
                                                                            onRedeem={_handleRedeemInvest}
                                                                            onClickCard={() => _handleSelectFund(item)}
                                                                            selectedFund={selectedFund}
                                                                            isCardView={isCardView}
                                                                            key={key} />
                                                                    </div>
                                                                </>
                                                            ))
                                                        }
                                                    </div>
                                                ) : (
                                                    <EmptyScreen
                                                        img={EmptyPortfolio}
                                                        title="No holdings !"
                                                        description={`No active holdings found.Try again later`} />
                                                )
                                            }
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-4 d-md-block d-none" id="detailed-fund-view">
                                        <DetailedViewCard />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }

                {
                    showInvestModal === false && showRedeemModal === false &&
                    showPaymentModal === false && selectedFund?.name &&
                    <OffcanvasDetailViewModal title="Portfolio Detail">
                        <DetailedViewCard />
                    </OffcanvasDetailViewModal>
                }

                {
                    // MF invest modal
                    showInvestModal && investType == "Fund" &&
                    <MFInvestModal fundSummary={fundSummary}
                        reinvest={true}
                        investmentType={2}
                        availableInvestmentTypes={available_investment_types}
                        onCloseModal={_handleCloseInvestModal} />
                }
                {
                    //goal invest modal 
                    showInvestModal && investType == "Goal" &&
                    <GoalInvestModal goalId={goalId}
                        reinvest={true}
                        summary={goalSummary}
                        onCloseModal={_handleCloseInvestModal} />
                }
                {
                    //goal invest modal 
                    showInvestModal && investType == "Basket" &&
                    <GoalInvestModal goalId={goalId}
                        reinvest={true}
                        summary={goalSummary}
                        onCloseModal={_handleCloseInvestModal} />
                }

                {/* Redeeem Invest Modal */}
                {
                    showRedeemModal &&
                    <RedeemModal summary={redeemSummary}
                        id={investType == "Fund" ? fundId : goalId}
                        investmentType={investType}
                        onCloseModal={_handleCloseRedeemModal} />
                }

                {
                    //Payment status modal
                    showPaymentModal &&
                    <PaymentStatusModal type={paymentModalType}
                        investmentType={investType}
                        onClose={(() => setShowPaymentModal(false))} />
                }

                {
                    //Redeem status modal
                    showRedeemStatusModal &&
                    <RedeemStatusModal
                        investmentType={investType}
                        onClose={(() => setShowRedeemStatusModal(false))} />
                }

                {
                    showImportPortfolioModal &&
                    <ExternalFundImportModal
                        onImportSuccess={_handleUpdatePortfolio}
                        onClose={_handleCloseImportPortfolioModal} />
                }

                {/* Update nominee info modal */}
                {/* {
                    showNomineeInfoModal &&
                    <UpdateNomineeInfoModal onClose={() => setShowNomineeInfoModal(false)} />
                } */}

            </div>
            <Footer />
        </>
    );
};

export default Portfolio;